import { CmsSubmissionState } from '@/data/models/cms';
import { IFormat, IMerchandise } from '@/data/models/format';
import { IGenre } from '@/data/models/genre';
import { IMerchandiseType } from '@/data/models/merchandiseType';
import { AlbumState } from '@/data/models/albumState';
import { ILabel } from '@/data/models/label';
import { IAlbum } from '@/data/models/album';
import _ from 'lodash';
import { ChangeRequestState } from '@/data/models/changeRequestState';

export function convertLabelToOption(label: ILabel) {
  if (!label) return label;
  const parentName = label.parentOrgName ? `<<${label.parentOrgName}>>` : '';
  return { value: label.adminOrgId, label: `${label.name} ${parentName}` };
}

export function buildCopyrightLine(
  year: number | null | undefined,
  line: string | null | undefined
): string {
  const copySign = '\u00A9';
  return buildLegalLine(copySign, year, line);
}

export function buildPhonographicCopyrightLine(
  year: number | null | undefined,
  line: string | null | undefined
): string {
  const phonoSign = '\u2117';
  return buildLegalLine(phonoSign, year, line);
}

function buildLegalLine(
  copySign: string,
  year: number | null | undefined,
  line: string | null | undefined
): string {
  // TODO isn't this just `${copySign} ${year} ${line}` ?
  const whitespace = ' ';
  const hasYear = year && year > 0;
  const hasLine = line && line.length > 0;

  if (!hasYear && !hasLine) {
    return '';
  }

  let legalLine = copySign;

  if (hasYear) {
    legalLine = legalLine + whitespace + year.toString();
  }

  if (hasLine) {
    legalLine = legalLine + whitespace + line;
  }

  return legalLine;
}

export function getAlbumStateBadgeClass(albumState: string): string {
  switch (albumState) {
    case AlbumState.Draft:
    case CmsSubmissionState.Draft:
    case ChangeRequestState.Draft:
      return 'badge-default';
    case AlbumState.PendingImport:
    case ChangeRequestState.PendingImport:
      return 'badge-pending-import';
    case CmsSubmissionState.QCApproved:
    case CmsSubmissionState.QCEditApproved:
      return 'badge-approve';
    case CmsSubmissionState.Released:
    case AlbumState.Released:
      return 'badge-ok';
    case AlbumState.UnderReview:
    case CmsSubmissionState.QCAnalyzing:
    case CmsSubmissionState.QCUnderReview:
    case CmsSubmissionState.QCPendingReview:
      return 'badge-alert';
    case CmsSubmissionState.Paused:
      return 'badge-pause';
    case CmsSubmissionState.PendingINDMAImport:
      return 'badge-pending-import';
    case AlbumState.Rejected:
    case AlbumState.Error:
    case CmsSubmissionState.Rejected:
    case CmsSubmissionState.Error:
    case CmsSubmissionState.Invalid:
    case CmsSubmissionState.Deleted:
    case CmsSubmissionState.Reviewed:
    case ChangeRequestState.Error:
    case ChangeRequestState.Deleted:
      return 'badge-error';
    case CmsSubmissionState.External:
      return 'badge-external';
    case ChangeRequestState.Submitted:
      return 'badge-submitted';
    default:
      return 'badge-default';
  }
}

export function getMerchandiseDisplayName(merchandise?: IMerchandise | null) {
  return merchandise?.name;
}

export function getFormatDisplayName(format?: IFormat | null) {
  return format?.name;
}

export function getSubTypeName(merchandiseType?: IMerchandiseType | null) {
  return merchandiseType?.name;
}

export function formatTitleVersions(
  versions?: string[],
  targetAccount?: string | null
) {
  const normalizedVersions = versions ?? [];
  const formattedTargetAccount = targetAccount ? ` (${targetAccount})` : '';
  return `${normalizedVersions.map((v: string) => ` (${v})`).join('')}${formattedTargetAccount}`;
}

export function formatGenre(genre?: IGenre | null) {
  if (!genre) return '';
  return `${genre?.masterGenre} > ${genre?.name}`;
}

export function isAlbumReadOnlyStates(albumState: AlbumState) {
  return [
    AlbumState.Released,
    AlbumState.PendingImport,
    AlbumState.Error,
  ].includes(albumState);
}

export function isEditableAlbumReadOnlyState(albumState: AlbumState) {
  return [AlbumState.PendingImport].includes(albumState);
}

export function getMerchDefaultDisplayName(album: IAlbum | undefined) {
  if (_.isNil(album)) return '';
  return [
    album?.title,
    album?.albumMetaData?.versions?.join(', '),
    album?.merchandiseMetadata?.color,
    album?.merchandiseMetadata?.merchandiseType?.name,
    album?.merchandiseMetadata?.size,
  ]
    .filter((val) => val && String(val).trim() !== '')
    .map((val) => val?.trim())
    .join(' ');
}

export const getActiveChangeRequestUrl = (
  albumId: string | undefined,
  isMerchandise = false
) => {
  if (!albumId) return '';

  if (isMerchandise) {
    return `/Merchandise/ChangeRequest/${albumId}`;
  }
  return `/Physical/ChangeRequest/Album/${albumId}`;
};

export const getChangeRequestDetailsUrl = (
  albumId: string | undefined,
  changeRequestId: number,
  isMerchandise = false
) => {
  if (!albumId || !changeRequestId) return '';

  if (isMerchandise) {
    return `/Merchandise/ChangeRequestDetails/${albumId}/ChangeRequest/${changeRequestId}`;
  }
  return `/Physical/ChangeRequestDetails/Album/${albumId}/ChangeRequest/${changeRequestId}`;
};
